import { DateTime } from "luxon";
import { useMemo } from "react";

import useDateContext from "@/providers/data/DateContext";
import { useGetFormattedPassenger } from "@/requests/passengers.queries";
import { useGetNewPassengers } from "@/requests/transport.queries";
import { PassengerExtras } from "@/types/global";
import { IPlanPassenger } from "@/types/tasks.types";

export const useParsePassengers = ({ date, passengerId }: { date?: DateTime; passengerId?: string | null }) => {
    const { selectedDate } = useDateContext();

    const {
        data: dateData,
        isFetching: dateFetching,
        isPending: datePending,
        refetch: dateRefetch,
    } = useGetNewPassengers({
        date: date || selectedDate,
        enabled: !passengerId,
    });

    const {
        data: populatedData,
        isFetching: populatedFetching,
        isPending: populatedPending,
        refetch: populatedRefetch,
    } = useGetFormattedPassenger({
        passengerId: passengerId || null,
    });

    const data = useMemo(() => {
        return passengerId ? (populatedData && [populatedData]) || [] : dateData || [];
    }, [passengerId, populatedData, dateData]);
    const loading = passengerId ? populatedFetching || populatedPending : dateFetching || datePending;
    const refetch = passengerId ? populatedRefetch : dateRefetch;

    const reloadTable = () => {
        refetch();
    };

    const passengers: IPlanPassenger<"api">[] = useMemo(() => {
        if (!data.length) return [];

        return data.map((passenger) => ({
            ...passenger,
            pickUp: (passenger.pickUp && DateTime.fromISO(passenger.pickUp)) || null,
            dropOff: (passenger.dropOff && DateTime.fromISO(passenger.dropOff)) || null,
            flightTime: (passenger.flightTime && DateTime.fromISO(passenger.flightTime)) || null,
            extras: passenger.extras || PassengerExtras.DEFAULT,
            driverId: passenger.driverId || null,
        }));
    }, [data]);

    return {
        passengers,
        reloadTable,
        loading,
    };
};
